import { Box } from '@material-ui/core'
import { Loader2 } from 'lucide-react'
import { useSnackbar } from 'notistack'
import { ChangeEvent, forwardRef, useCallback, useEffect, useImperativeHandle } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useIntl } from 'react-intl'
import { Link } from 'react-router-dom'
import { Button, FormattedText, FormControl, Loader, ToggleSwitch, Typography } from 'src/modules/core/components'
import { useUpdateUserPreferences, useUserPreferences } from 'src/modules/preferences/hooks/use-user-preferences'
import type { AutoActivatePreferencesFormData } from 'src/modules/user/components/settings-profile-info-form/settings-profile-info-form.interface'
import type { OtherSettingsAutoActivateFormHandle } from 'src/modules/user/components/settings-profile-info/other-settings-form-handles'
import { buildAutoActivatedRemindersDashboardPath } from 'src/modules/user/constants/dashboard-tab-navigation'
import { useRevokeAllActiveStandingConsents } from 'src/modules/user/hooks/use-revoke-all-active-standing-consents'
import { useStandingConsentActiveStatus } from 'src/modules/user/hooks/use-standing-consent-active-status'
import classes from './settings-set-and-forget-reminders-card.module.scss'

export type SettingsSetAndForgetRemindersCardProps = {
  hideSubmitButton?: boolean
  onDirtyChange?: (dirty: boolean) => void
}

export const SettingsSetAndForgetRemindersCard = forwardRef<
  OtherSettingsAutoActivateFormHandle,
  SettingsSetAndForgetRemindersCardProps
>(function SettingsSetAndForgetRemindersCard({ hideSubmitButton = false, onDirtyChange }, ref) {
  const { formatMessage } = useIntl()
  const { enqueueSnackbar } = useSnackbar()
  const preferencesQuery = useUserPreferences()
  const updatePreferences = useUpdateUserPreferences()
  const standingConsentStatus = useStandingConsentActiveStatus()
  const revokeAllStandingConsents = useRevokeAllActiveStandingConsents()
  const hasActive = !standingConsentStatus.isLoading && standingConsentStatus.data?.hasActive === true

  const {
    control,
    handleSubmit,
    reset,
    watch,
    getValues,
    formState: { isDirty },
  } = useForm<AutoActivatePreferencesFormData>({
    defaultValues: {
      auto_activate_when_clear: false,
    },
  })

  useEffect(() => {
    const data = preferencesQuery.data
    if (!data) {
      return
    }
    reset({
      auto_activate_when_clear: data.user_preferences.auto_activate_when_clear ?? false,
    })
  }, [preferencesQuery.data, reset])

  const persistIfDirty = useCallback(async (): Promise<boolean> => {
    if (!isDirty || !preferencesQuery.data) {
      return false
    }
    const data = getValues()
    await updatePreferences.mutateAsync({
      google_calendar_settings_customized: preferencesQuery.data.user_preferences.google_calendar_settings_customized,
      auto_activate_when_clear: data.auto_activate_when_clear,
    })
    reset(data)
    return true
  }, [getValues, isDirty, preferencesQuery.data, reset, updatePreferences])

  useImperativeHandle(
    ref,
    () => ({
      submitIfDirty: persistIfDirty,
    }),
    [persistIfDirty],
  )

  useEffect(() => {
    onDirtyChange?.(isDirty)
  }, [isDirty, onDirtyChange])

  const onSubmit = (data: AutoActivatePreferencesFormData) => {
    if (!preferencesQuery.data) {
      return
    }
    updatePreferences
      .mutateAsync({
        google_calendar_settings_customized: preferencesQuery.data.user_preferences.google_calendar_settings_customized,
        auto_activate_when_clear: data.auto_activate_when_clear,
      })
      .then(() => {
        reset(data)
        if (!hideSubmitButton) {
          enqueueSnackbar(formatMessage({ id: 'settings.saved' }), { variant: 'success' })
        }
      })
      .catch(() => {
        /* errors surface via global handlers / invalidation */
      })
  }

  if (!preferencesQuery.isLoading && (preferencesQuery.isError || !preferencesQuery.data)) {
    return null
  }

  if (preferencesQuery.isLoading) {
    return <Loader isLoading={true} />
  }

  const autoActivateWhenClear = watch('auto_activate_when_clear')

  return (
    <Box
      className={classes.card}
      component="section"
      aria-label={formatMessage({ id: 'settings.setAndForgetReminders.title' })}
      data-testid="set-and-forget-reminders-card"
    >
      <Typography variant="h5" className={classes.title}>
        <FormattedText id="settings.setAndForgetReminders.title" />
      </Typography>

      <form onSubmit={handleSubmit(onSubmit)}>
        <FormControl
          label={formatMessage({ id: 'settings.setAndForgetReminders.toggleLabel' })}
          labelClassName={classes.label}
        >
          <Box className={classes.row}>
            <Controller
              control={control}
              name="auto_activate_when_clear"
              render={({ field: { onChange, value, ref: fieldRef } }) => (
                <ToggleSwitch
                  ref={fieldRef}
                  checked={value}
                  labelClassName={classes.switchLabel}
                  onChange={(e: ChangeEvent<HTMLInputElement>) => onChange(e.target.checked)}
                />
              )}
            />
            <Typography variant="body" className={classes.infoText}>
              <FormattedText id={autoActivateWhenClear ? 'common.enabled' : 'common.disabled'} />
            </Typography>
          </Box>
        </FormControl>

        <Typography variant="body" className={classes.helperText} data-testid="set-and-forget-helper-text">
          <FormattedText
            id={
              autoActivateWhenClear
                ? 'settings.setAndForgetReminders.helperOn'
                : 'settings.setAndForgetReminders.helperOff'
            }
          />
        </Typography>

        {hasActive && (
          <div className={classes.forgetAllRow}>
            <button
              type="button"
              className={classes.linkPlaceholder}
              disabled={revokeAllStandingConsents.isLoading}
              onClick={() =>
                revokeAllStandingConsents.mutate(undefined, {
                  onSuccess: () => {
                    enqueueSnackbar(formatMessage({ id: 'settings.setAndForgetReminders.forgetAllApproved.success' }), {
                      variant: 'success',
                    })
                  },
                  onError: () => {
                    /* errors surface via global handlers / invalidation */
                  },
                })
              }
              data-testid="set-and-forget-forget-all-approved"
            >
              <FormattedText id="settings.setAndForgetReminders.forgetAllApproved" />
            </button>
            {revokeAllStandingConsents.isLoading && (
              <Loader2 className={classes.forgetAllSpinner} size={16} aria-label="Loading" />
            )}
          </div>
        )}

        <Link
          to={buildAutoActivatedRemindersDashboardPath()}
          className={classes.linkPlaceholder}
          data-testid="set-and-forget-auto-activated-link"
        >
          <FormattedText id="settings.setAndForgetReminders.viewAutoActivatedLink" />
        </Link>

        {!hideSubmitButton && (
          <Button
            type="submit"
            className={classes.button}
            widthType="content"
            isLoading={updatePreferences.isLoading}
            disabled={!isDirty}
          >
            <FormattedText id="common.save" />
          </Button>
        )}
      </form>
    </Box>
  )
})
